import { OtlDateFormField as DateFormField } from "./OtlDateFormField";
import { OtlTextFormField as TextFormField } from "./OtlTextFormField";
import {
  Button,
  FormLabel,
  Grid,
  Skeleton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Box } from "@mui/system";
import React from "react";
import { OtlSubmissionForm } from "./OtlSubmissionForm";
import { communityApi, getOtlSettings } from "../common/api";
import {
  RadioFormField,
  SelectFormField,
  SelectFormFieldOption,
} from "../common/components/FormFields";
import { FormFieldWrapper } from "../common/components/FormFields/FormFieldWrapper";
import {
  MuiCake,
  MuiEmail,
  MuiHome,
  MuiLocalGasStation,
  MuiLocalGroceryStore,
  MuiMap,
  MuiNumber,
  MuiPerson,
  MuiPhone,
} from "../common/icons";
import { useAsyncData } from "../common/networking";
import { HouseholdMemberList } from "./HouseholdMemberList";
import { OtlFormHeader } from "./OtlFormHeader";
import {
  getGasStoreOptionsForCommunity,
  getGroceryStoreOptionsForCommunity,
} from "./storeFetcher";
import { OtlConsent } from "./OtlConsent";
import { OtlAddressFields } from "./OtlAddressFields";

const INITIAL_VALUES = {
  physicalAddress: { provinceOrTerritory: 12, country: "CA" },
  mailingAddress: { provinceOrTerritory: 12, country: "CA" },
};

export interface OtlFormProps {}

export const OtlForm: React.FC<OtlFormProps> = ({}) => {
  const { data: communities, isLoading: isCommunitiesLoading } = useAsyncData(
    () => communityApi.getCommunities(),
    [],
  );

  const theme = useTheme();
  const sm = useMediaQuery(theme.breakpoints.up("sm"));
  const md = useMediaQuery(theme.breakpoints.up("md"));

  const { data: otlSettings, isLoading: isOtlSettingsLoading } = useAsyncData(
    () => getOtlSettings(),
    [],
  );

  if (isOtlSettingsLoading || isCommunitiesLoading) {
    return (
      <Stack gap={2}>
        <Skeleton variant="rectangular" height={100} />
        <Skeleton variant="rectangular" height={100} />
        <Skeleton variant="rectangular" height={100} />
      </Stack>
    );
  }

  return (
    <>
      {otlSettings?.enabled ? (
        <>
          <Box>
            <Box mb={2}>
              <OtlFormHeader />
            </Box>
            <OtlSubmissionForm
              period={`${otlSettings.period}:${otlSettings.startDate}:${otlSettings.endDate}`}
              initialValues={INITIAL_VALUES}
            >
              {({
                handleSubmit,
                hasValidationErrors,
                values,
                submitting,
                locked,
              }) => (
                <form onSubmit={handleSubmit}>
                  <fieldset
                    disabled={submitting || locked}
                    style={{ border: 0, margin: 0, padding: 0, minWidth: 0 }}
                  >
                    <Grid container spacing={2} mb={2}>
                      <Grid item xs={12} sm={12} md={true}>
                        <TextFormField
                          name="firstName"
                          label="First Name"
                          required
                          icon={<MuiPerson />}
                        />
                      </Grid>
                      <Grid item xs={12} sm={12} md={true}>
                        <TextFormField
                          name="lastName"
                          label="Last Name"
                          required
                          noIcon
                        />
                      </Grid>
                      <Grid item xs={12} sm={true}>
                        <SelectFormField
                          name="community"
                          label="Community for voucher pickup"
                          required
                          icon={<MuiMap />}
                          isLoading={isCommunitiesLoading}
                          options={communities
                            ?.filter(
                              (community) => community.name !== "Outside ISR",
                            )
                            .map(
                              (d): SelectFormFieldOption => ({
                                value: d.id,
                                text: d.name,
                              }),
                            )}
                        />
                      </Grid>
                    </Grid>
                    <Grid container spacing={2} mb={2}>
                      <Grid item xs={12} sm={6}>
                        <TextFormField
                          name="fileNumber"
                          label="File#"
                          required={false}
                          icon={<MuiNumber />}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <DateFormField
                          name="dateOfBirth"
                          label="Date of Birth"
                          required
                          icon={<MuiCake />}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextFormField
                          name="email"
                          label="Email"
                          required
                          icon={<MuiEmail />}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextFormField
                          name="phoneNumber"
                          label="Phone"
                          required
                          icon={<MuiPhone />}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <OtlAddressFields
                          required
                          name="physicalAddress"
                          label="Physical Address"
                          communities={communities}
                          isCommunitiesLoading={isCommunitiesLoading}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <OtlAddressFields
                          required
                          name="mailingAddress"
                          label="Mailing Address"
                          noIcon={md}
                          communities={communities}
                          isCommunitiesLoading={isCommunitiesLoading}
                        />
                      </Grid>
                    </Grid>
                    <FormFieldWrapper>
                      <HouseholdMemberList
                        communities={communities}
                        isCommunitiesLoading={isCommunitiesLoading}
                      />
                    </FormFieldWrapper>

                    <Box mt={2}>
                      <RadioFormField
                        name="gasStorePreference"
                        label="Gas Store Preference"
                        icon={<MuiLocalGasStation />}
                        options={getGasStoreOptionsForCommunity(
                          communities,
                          String(values.community ?? ""),
                        )}
                      />
                    </Box>
                    <Box mt={2}>
                      <RadioFormField
                        name="groceryStorePreference"
                        label="Grocery Store Preference"
                        icon={<MuiLocalGroceryStore />}
                        options={getGroceryStoreOptionsForCommunity(
                          communities,
                          String(values.community ?? ""),
                        )}
                      />
                    </Box>

                    <Box ml={5} mt={2}>
                      <FormLabel>Emergency Contact</FormLabel>
                    </Box>
                    <Grid container mb={4} columnSpacing={2}>
                      <Grid item xs={12} sm={4}>
                        <TextFormField
                          name="emergencyContact.firstName"
                          required
                          label="First Name"
                          icon={<MuiPerson />}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextFormField
                          name="emergencyContact.lastName"
                          required
                          label="Last Name"
                          noIcon
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextFormField
                          name="emergencyContact.phoneNumber"
                          required
                          label="Phone Number"
                          noIcon={sm}
                        />
                      </Grid>
                    </Grid>

                    <Box pb={2}>
                      <OtlConsent />
                    </Box>

                    <Box width={"100%"} display={"flex"} justifyContent={"end"}>
                      <Button
                        type="submit"
                        disabled={hasValidationErrors || submitting || locked}
                        variant="contained"
                        size="large"
                      >
                        {submitting ? "Submitting…" : "Submit"}
                      </Button>
                    </Box>
                  </fieldset>
                </form>
              )}
            </OtlSubmissionForm>
          </Box>
        </>
      ) : (
        <>
          <Typography component="h1" variant="h5" fontWeight="bold" mb={2}>
            Fall 2026 On-the-Land support applications are now closed.
          </Typography>
          <Typography paragraph>
            Thank you to everyone who applied. If you received a confirmation
            email, your application was received and you don’t need to do anything
            else.
          </Typography>
          <Typography paragraph>
            Applications are being reviewed.{" "}
            <strong>
              Only those who have NOT been approved will be contacted (on or before
              October 15).
            </strong>
          </Typography>
          <Typography paragraph>
            Approved households can pick up their vouchers in the community they
            selected starting Friday, October 16.{" "}
            <strong>
              Vouchers must be picked up by Friday, October 30.
            </strong>{" "}
            Only the applicant can pick up the voucher.
          </Typography>
          <Typography paragraph>
            If you have questions about your application, call{" "}
            <a href="tel:+18677777000">867.777.7000</a> or email{" "}
            <a href="mailto:info@inuvialuit.com">info@inuvialuit.com</a>.
          </Typography>
          <Typography>Inuvialuit Regional Corporation</Typography>
        </>
      )}
    </>
  );
};
